// ═══════════════════════════════════════════════════════════
// Public Portal — Submit a Request (multi-step form)
// ═══════════════════════════════════════════════════════════

import { useState, FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { IconArrowLeft, IconArrowRight, IconCheck, IconPaperclip, IconX, IconAlertTriangle } from '@tabler/icons-react';
import { createRequest } from '../../api/requests';
import { maskPhone, formatPhone } from '../../lib/format';
import { uploadDocument } from '../../api/documents';
import { FIELDS } from '../../api/fields';
import { usePublicAuthStore } from '../../store/publicAuthStore';
import { useJurisdiction } from '../../contexts/JurisdictionContext';

type Step = 'request' | 'requester' | 'review' | 'submitted';

interface FormData {
  // Request
  subject: string;
  description: string;
  preferredFormat: string;
  willPayFees: boolean;
  feeLimit: string;

  // Requester
  name: string;
  email: string;
  confirmEmail: string;
  phone: string;
  organization: string;
  wantsEmailUpdates: boolean;

  // Files
  files: File[];
}

const FORMATS = [
  { value: 'electronic', label: 'Electronic — PDF / files via email' },
  { value: 'paper',      label: 'Paper copies — mail or pickup' },
  { value: 'inspect',    label: 'Inspection only — no copies needed' },
];

const STEPS: { id: Step; label: string }[] = [
  { id: 'request',   label: 'Your Request' },
  { id: 'requester', label: 'Your Information' },
  { id: 'review',    label: 'Review & Submit' },
];

function StepIndicator({ current }: { current: Step }) {
  const stepIds = STEPS.map(s => s.id);
  const currentIdx = stepIds.indexOf(current);
  if (current === 'submitted') return null;
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 32, gap: 0 }}>
      {STEPS.map((step, i) => {
        const done = i < currentIdx;
        const active = i === currentIdx;
        return (
          <div key={step.id} style={{ display: 'flex', alignItems: 'center' }}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <div style={{
                width: 32, height: 32, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 14, fontWeight: 700,
                background: done || active ? 'var(--color-primary-700)' : 'var(--color-neutral-200)',
                color: done || active ? 'white' : 'var(--color-neutral-400)',
              }}>
                {done ? <IconCheck size={14} /> : i + 1}
              </div>
              <div style={{ fontSize: 12, marginTop: 4, fontWeight: active ? 600 : 400, color: active || done ? 'var(--color-primary-700)' : 'var(--color-neutral-400)' }}>
                {step.label}
              </div>
            </div>
            {i < STEPS.length - 1 && (
              <div style={{ width: 80, height: 2, background: done ? 'var(--color-primary-700)' : 'var(--color-neutral-200)', marginBottom: 16, flexShrink: 0 }} />
            )}
          </div>
        );
      })}
    </div>
  );
}

export default function Submit() {
  const publicUser = usePublicAuthStore(s => s.user);
  const jurisdiction = useJurisdiction();
  const [step, setStep] = useState<Step>('request');
  const [confirmedRequestNumber, setConfirmedRequestNumber] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [form, setForm] = useState<FormData>(() => ({
    subject: '', description: '', preferredFormat: 'electronic',
    willPayFees: false, feeLimit: '25',
    // Pre-fill from portal account if signed in
    name: publicUser ? `${publicUser.firstName} ${publicUser.lastName}`.trim() : '',
    email: publicUser?.email ?? '',
    confirmEmail: publicUser?.email ?? '',
    phone: '', organization: '',
    wantsEmailUpdates: true, files: [],
  }));

  function update<K extends keyof FormData>(key: K, value: FormData[K]) {
    setForm(f => ({ ...f, [key]: value }));
  }

  function onFileAdd(e: React.ChangeEvent<HTMLInputElement>) {
    if (e.target.files) {
      update('files', [...form.files, ...Array.from(e.target.files)]);
      e.target.value = '';
    }
  }

  function removeFile(idx: number) {
    update('files', form.files.filter((_, i) => i !== idx));
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setSubmitError(null);

    try {
      // Split name into first/last (best-effort)
      const nameParts = form.name.trim().split(/\s+/);
      const firstName = nameParts[0] ?? '';
      const lastName = nameParts.slice(1).join(' ');

      const { request } = await createRequest({
        requesterFirstName: firstName || 'Anonymous',
        requesterLastName: lastName,
        requesterEmail:    form.email || undefined,
        requesterPhone:    form.phone || undefined,
        requesterOrg:      form.organization || undefined,
        requesterType:     form.organization ? 'business' : 'individual',
        subject:           form.subject,
        description:       form.description,
        dateReceived:      new Date().toISOString().split('T')[0],
        channel:           'portal',
        isAnonymous:       !form.name && !form.email,
        jurisdictionId:    import.meta.env.VITE_JURISDICTION_ID ?? '1',
        requestType:       'FOIA',
        isRegistered:      !!publicUser,
        publicUserId:      publicUser ? String(publicUser.recordId) : undefined,
      }, { anonymous: true });

      const reqRow = request as Record<string, unknown>;
      const reqNumber = String(reqRow[FIELDS.requests.requestNumber] ?? reqRow['request_number'] ?? '');

      // Upload any attachments (fire-and-forget — don't block confirmation)
      if (form.files.length > 0) {
        const reqId = (request as Record<string, unknown>)['record_id'];
        form.files.forEach(file => {
          uploadDocument(file, { anonymous: true }).catch(console.warn);
          void reqId; // document-request linkage wired in foiaDocuments.ts when used from staff
        });
      }

      setConfirmedRequestNumber(reqNumber);
      setStep('submitted');
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Submission failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  if (step === 'submitted') {
    return (
      <div style={{ maxWidth: 560, margin: '0 auto', padding: '60px 24px', textAlign: 'center' }}>
        <div style={{ width: 64, height: 64, borderRadius: '50%', background: 'var(--cds-green-50)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
          <IconCheck size={28} style={{ color: 'var(--cds-green-700)' }} />
        </div>
        <h1 style={{ fontSize: 24, fontWeight: 700, color: 'var(--color-neutral-900)', margin: '0 0 8px' }}>Request Submitted!</h1>
        <p style={{ fontSize: 14, color: 'var(--color-neutral-500)', margin: '0 0 4px' }}>
          Your request number is:
        </p>
        <div style={{ fontSize: 24, fontWeight: 700, fontFamily: 'monospace', color: 'var(--color-primary-700)', margin: '8px 0 20px' }}>
          {confirmedRequestNumber}
        </div>
        <p style={{ fontSize: 14, color: 'var(--color-neutral-500)', margin: '0 0 24px', lineHeight: 1.6 }}>
          {form.wantsEmailUpdates && form.email
            ? `We'll send updates to ${form.email}.`
            : 'Save your request number to check status at any time.'}{' '}
          We are required to acknowledge your request within 5 business days.
        </p>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
          <Link to={`/portal/track/${confirmedRequestNumber}`} className="btn btn-primary">
            Track My Request
          </Link>
          <Link to="/portal" className="btn btn-secondary">
            Return to Portal
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 640, margin: '0 auto', padding: '40px 24px' }}>
      <Link to="/portal" style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 14, color: 'var(--color-primary-700)', textDecoration: 'none', marginBottom: 28 }}>
        <IconArrowLeft size={13} /> Back to portal
      </Link>

      <h1 style={{ fontSize: 24, fontWeight: 700, color: 'var(--color-neutral-900)', margin: '0 0 4px' }}>Submit a Records Request</h1>
      <p style={{ fontSize: 14, color: 'var(--color-neutral-500)', margin: '0 0 28px' }}>{jurisdiction?.name ?? 'the jurisdiction'} · {jurisdiction?.lawName ?? 'Freedom of Information Act'}</p>

      <StepIndicator current={step} />

      <form onSubmit={onSubmit}>
        {/* ── Step 1: Request ── */}
        {step === 'request' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div className="form-field" style={{ marginBottom: 0 }}>
              <label className="form-label required">Subject / Brief Description</label>
              <input
                type="text"
                className="form-input"
                value={form.subject}
                onChange={e => update('subject', e.target.value)}
                required
                placeholder="e.g. Police incident reports for March 2026"
              />
              <div className="form-hint">A short title for your request.</div>
            </div>

            <div className="form-field" style={{ marginBottom: 0 }}>
              <label className="form-label required">Describe the Records You're Requesting</label>
              <textarea
                className="form-textarea"
                rows={6}
                value={form.description}
                onChange={e => update('description', e.target.value)}
                required
                placeholder="Be as specific as possible — include relevant dates, names, subjects, departments, or document types. More specific requests are fulfilled faster."
              />
            </div>

            <div className="form-field" style={{ marginBottom: 0 }}>
              <label className="form-label">Preferred Format</label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {FORMATS.map(f => (
                  <label key={f.value} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 14, cursor: 'pointer', padding: '8px 12px', borderRadius: 4, border: `1.5px solid ${form.preferredFormat === f.value ? 'var(--color-primary-700)' : 'var(--color-neutral-200)'}`, background: form.preferredFormat === f.value ? 'var(--color-primary-50, #F7F8FD)' : 'white' }}>
                    <input type="radio" name="format" value={f.value} checked={form.preferredFormat === f.value} onChange={() => update('preferredFormat', f.value)} />
                    {f.label}
                  </label>
                ))}
              </div>
            </div>

            <div className="form-field" style={{ marginBottom: 0 }}>
              <label className="form-label">Supporting Documents (optional)</label>
              <div style={{
                border: '2px dashed var(--color-neutral-300)', borderRadius: 4, padding: '16px', textAlign: 'center',
                cursor: 'pointer', background: 'var(--color-neutral-50)',
              }}
                onClick={() => document.getElementById('file-upload')?.click()}
              >
                <IconPaperclip size={20} style={{ color: 'var(--color-neutral-400)', marginBottom: 6 }} />
                <div style={{ fontSize: 14, color: 'var(--color-neutral-500)' }}>Click to attach files</div>
                <div style={{ fontSize: 12, color: 'var(--color-neutral-400)', marginTop: 2 }}>PDF, Word, or image · max 10 MB each</div>
                <input id="file-upload" type="file" multiple style={{ display: 'none' }} onChange={onFileAdd} accept=".pdf,.doc,.docx,.jpg,.png" />
              </div>
              {form.files.length > 0 && (
                <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {form.files.map((file, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', background: 'var(--cds-green-50)', borderRadius: 4, fontSize: 12 }}>
                      <IconPaperclip size={12} style={{ color: 'var(--cds-green-700)' }} />
                      <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'var(--cds-green-700)' }}>{file.name}</span>
                      <span style={{ color: 'var(--color-neutral-500)' }}>{(file.size / 1024).toFixed(0)} KB</span>
                      <button type="button" onClick={() => removeFile(i)} style={{ border: 'none', background: 'none', cursor: 'pointer', padding: 2, color: 'var(--color-neutral-400)' }}>
                        <IconX size={12} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div style={{ padding: '12px 16px', background: 'var(--cds-yellow-50)', borderRadius: 4, border: '1px solid #FCD8A3', fontSize: 14, color: 'var(--cds-yellow-500)' }}>
              <strong>Note on fees:</strong> Research and copying fees may apply for large requests. You will be notified before any fees exceed $25.
            </div>

            <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 14, cursor: 'pointer', padding: '12px 16px', background: 'var(--color-neutral-50)', borderRadius: 4, border: '1px solid var(--color-neutral-200)' }}>
              <input type="checkbox" checked={form.willPayFees} onChange={e => update('willPayFees', e.target.checked)} style={{ marginTop: 2 }} />
              <span style={{ color: 'var(--color-neutral-700)', lineHeight: 1.6 }}>
                I agree to pay reasonable fees for the search, review, and duplication of records, up to <strong>${form.feeLimit || '25'}</strong>. I will be notified before any charges exceed this amount.
              </span>
            </label>

            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button
                type="button"
                className="btn btn-primary"
                disabled={!form.subject || !form.description}
                onClick={() => setStep('requester')}
                style={{ gap: 6 }}
              >
                Next: Your Information <IconArrowRight size={14} />
              </button>
            </div>
          </div>
        )}

        {/* ── Step 2: Requester ── */}
        {step === 'requester' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {publicUser ? (
              <div style={{ padding: '12px 14px', background: 'var(--color-primary-50, #F7F8FD)', borderRadius: 4, border: '1px solid var(--color-primary-200, #D4DDFB)', fontSize: 14, color: 'var(--color-primary-700)' }}>
                Signed in as <strong>{publicUser.firstName} {publicUser.lastName}</strong> ({publicUser.email}). This request will appear in your account.
              </div>
            ) : (
              <div style={{ padding: '12px 14px', background: 'var(--color-primary-50, #F7F8FD)', borderRadius: 4, border: '1px solid var(--color-primary-200, #D4DDFB)', fontSize: 14, color: 'var(--color-primary-700)' }}>
                You are not required to provide your name or explain your reason for requesting records.
                Your identity will not affect whether your request is fulfilled.
              </div>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 20px' }}>
              <div className="form-field" style={{ marginBottom: 0 }}>
                <label className="form-label">Your Name</label>
                <input
                  type="text"
                  className="form-input"
                  value={form.name}
                  onChange={e => update('name', e.target.value)}
                  placeholder="Optional"
                  readOnly={!!publicUser}
                  style={publicUser ? { background: 'var(--color-neutral-50)', color: 'var(--color-neutral-600)' } : {}}
                />
              </div>
              <div className="form-field" style={{ marginBottom: 0 }}>
                <label className="form-label">Organization (if applicable)</label>
                <input
                  type="text"
                  className="form-input"
                  value={form.organization}
                  onChange={e => update('organization', e.target.value)}
                  placeholder="Optional"
                />
              </div>
            </div>

            <div className="form-field" style={{ marginBottom: 0 }}>
              <label className="form-label">Email Address</label>
              <input
                type="email"
                className="form-input"
                value={form.email}
                onChange={e => { update('email', e.target.value); update('confirmEmail', e.target.value); }}
                placeholder="Recommended — for status updates and document delivery"
                readOnly={!!publicUser}
                style={publicUser ? { background: 'var(--color-neutral-50)', color: 'var(--color-neutral-600)' } : {}}
              />
              {publicUser && <div className="form-hint">Email is set from your account and cannot be changed here.</div>}
            </div>

            {!publicUser && form.email && (
              <div className="form-field" style={{ marginBottom: 0 }}>
                <label className="form-label required">Confirm Email Address</label>
                <input
                  type="email"
                  className="form-input"
                  value={form.confirmEmail}
                  onChange={e => update('confirmEmail', e.target.value)}
                  required={!!form.email}
                />
                {form.confirmEmail && form.email !== form.confirmEmail && (
                  <div className="form-error">Email addresses do not match.</div>
                )}
              </div>
            )}

            <div className="form-field" style={{ marginBottom: 0 }}>
              <label className="form-label">Phone Number</label>
              <input
                type="tel"
                className="form-input"
                value={form.phone}
                onInput={maskPhone}
                onChange={e => update('phone', e.target.value)}
                placeholder="(555) 123-4567"
              />
            </div>

            {form.email && (
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, fontSize: 14, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={form.wantsEmailUpdates}
                  onChange={e => update('wantsEmailUpdates', e.target.checked)}
                  style={{ marginTop: 2 }}
                />
                <span style={{ color: 'var(--color-neutral-700)', lineHeight: 1.5 }}>
                  Send me email notifications when my request status changes or documents are ready.
                </span>
              </label>
            )}

            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
              <button type="button" className="btn btn-secondary" onClick={() => setStep('request')} style={{ gap: 4 }}>
                <IconArrowLeft size={13} /> Back
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => setStep('review')}
                disabled={!!form.email && form.email !== form.confirmEmail}
                style={{ gap: 6 }}
              >
                Review Request <IconArrowRight size={14} />
              </button>
            </div>
          </div>
        )}

        {/* ── Step 3: Review ── */}
        {step === 'review' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div className="card" style={{ padding: 0 }}>
              <div style={{ padding: '10px 14px', borderBottom: '1px solid var(--color-neutral-100)' }}>
                <span style={{ fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--color-neutral-500)' }}>Your Request</span>
              </div>
              <div style={{ padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 8 }}>
                <div>
                  <div style={{ fontSize: 12, color: 'var(--color-neutral-400)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 2 }}>Subject</div>
                  <div style={{ fontSize: 14, color: 'var(--color-neutral-900)', fontWeight: 500 }}>{form.subject}</div>
                </div>
                <div>
                  <div style={{ fontSize: 12, color: 'var(--color-neutral-400)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 2 }}>Description</div>
                  <div style={{ fontSize: 14, color: 'var(--color-neutral-700)', lineHeight: 1.6 }}>{form.description}</div>
                </div>
                <div>
                  <div style={{ fontSize: 12, color: 'var(--color-neutral-400)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 2 }}>Format</div>
                  <div style={{ fontSize: 14, color: 'var(--color-neutral-700)' }}>{FORMATS.find(f => f.value === form.preferredFormat)?.label}</div>
                </div>
                {form.files.length > 0 && (
                  <div>
                    <div style={{ fontSize: 12, color: 'var(--color-neutral-400)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 2 }}>Attachments</div>
                    <div style={{ fontSize: 14, color: 'var(--color-neutral-700)' }}>{form.files.map(f => f.name).join(', ')}</div>
                  </div>
                )}
              </div>
            </div>

            <div className="card" style={{ padding: 0 }}>
              <div style={{ padding: '10px 14px', borderBottom: '1px solid var(--color-neutral-100)' }}>
                <span style={{ fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--color-neutral-500)' }}>Your Information</span>
              </div>
              <div style={{ padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 6 }}>
                {[
                  ['Name', form.name || 'Not provided'],
                  ['Email', form.email || 'Not provided'],
                  ['Phone', formatPhone(form.phone) || 'Not provided'],
                  ['Organization', form.organization || 'Not provided'],
                ].map(([label, value]) => (
                  <div key={label} style={{ display: 'flex', gap: 16 }}>
                    <span style={{ fontSize: 12, color: 'var(--color-neutral-400)', width: 90, flexShrink: 0 }}>{label}</span>
                    <span style={{ fontSize: 14, color: 'var(--color-neutral-700)' }}>{value}</span>
                  </div>
                ))}
              </div>
            </div>

            <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, fontSize: 14, cursor: 'pointer', padding: '12px 14px', background: 'var(--color-neutral-50)', borderRadius: 4, border: '1px solid var(--color-neutral-200)' }}>
              <input type="checkbox" required style={{ marginTop: 2 }} />
              <span style={{ color: 'var(--color-neutral-700)', lineHeight: 1.6 }}>
                I understand that submitting a public records request does not guarantee that all requested records exist or will be released.
                Records may be withheld in whole or in part under applicable exemptions.
              </span>
            </label>

            {submitError && (
              <div style={{ padding: '10px 14px', background: 'var(--cds-red-50)', borderRadius: 4, border: '1px solid #F7D5D0', display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 14, color: 'var(--cds-red-700)' }}>
                <IconAlertTriangle size={15} style={{ flexShrink: 0, marginTop: 1 }} />
                {submitError}
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <button type="button" className="btn btn-secondary" onClick={() => setStep('requester')} style={{ gap: 4 }} disabled={submitting}>
                <IconArrowLeft size={13} /> Back
              </button>
              <button type="submit" className="btn btn-primary" style={{ gap: 6 }} disabled={submitting}>
                {submitting ? 'Submitting…' : <><IconCheck size={14} /> Submit Request</>}
              </button>
            </div>
          </div>
        )}
      </form>
    </div>
  );
}
